import * as THREE from 'three';
import type { City, Place } from '../world/city';
import { militaryZone } from '../world/strategic-districts';
import type { PhysicsCity } from '../physics/world';
import type { Person, Population, TrafficVehicle } from './population';
import type { WalkNode } from './walking';
import type { Vec } from '../destruction/structure';
import { MilitarySupport } from './military-support';
import { BaseSecurity } from './base-security';
import { MilitaryMaritime } from './military-maritime';
import { MilitaryAviation } from './military-aviation';
import { MilitaryRelief } from './military-relief';
import { isMilitaryVehicle, MILITARY_SPECS } from '../render/military-vehicles';
import { overlapsRoadBody } from './traffic-space';
import { SpecialOperations } from './special-operations';
import { MILITARY_BALANCE as BALANCE } from './military-balance';
const UPRIGHT = new THREE.Quaternion();

export type Faction = 'friendly' | 'hostile';
export interface Soldier {
  id: string;
  person: Person;
  faction: Faction;
  index: number;
  state:
    | 'aboard'
    | 'base'
    | 'exiting'
    | 'inspection'
    | 'deployed'
    | 'returning'
    | 'incapacitated'
    | 'lost'
    | 'airborne'
    | 'withdrawn';
  role: string;
  assignment?: 'naval' | 'air';
  vehicle?: TrafficVehicle;
  seat?: number;
  home?: Place;
  duty?: Place;
  task: string;
  sequence: number;
  retry: number;
  checks: number;
  route: number[];
  cursor: number;
  goal?: Vec;
  planning?: Generator<void, number[]>;
  transition?: { from: THREE.Vector3; to: THREE.Vector3; time: number; boarding: boolean };
  cooldown: number;
  think: number;
  target?: string;
}
export interface Patrol {
  car: TrafficVehicle;
  crew: Soldier[];
  index: number;
  turns: number;
  phase: 'patrol' | 'response' | 'braking' | 'inspection' | 'boarding' | 'clearing';
  baseOnly?: boolean;
  destination?: string;
  timer: number;
  checks: number;
}
/** One finite peacetime roster. Seats and work are assignments of the original
 * people; reset is the only operation that creates a fresh friendly roster. */
export class Military {
  friendly: Soldier[] = [];
  byId = new Map<string, Soldier>();
  patrols: Patrol[] = [];
  time = 0;
  mobilised = false;
  log: { time: number; id: string; action: string }[] = [];
  private epoch = 0;
  private sense = 0;
  private places = new Map<number, Place[]>();
  readonly support: MilitarySupport;
  readonly security: BaseSecurity;
  readonly maritime: MilitaryMaritime;
  readonly air: MilitaryAviation;
  readonly relief: MilitaryRelief;
  readonly elite: SpecialOperations;
  private patrolStops: { node: number; name: string }[] = [];
  private roadRecovery = new Map<string, number>();
  clearanceRequests = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.support = new MilitarySupport(this);
    this.security = new BaseSecurity(this);
    this.maritime = new MilitaryMaritime(this);
    this.air = new MilitaryAviation(this);
    this.relief = new MilitaryRelief(this);
    this.elite = new SpecialOperations(this);
    for (const step of this.resetSteps()) void step;
  }
  private record(id: string, action: string): void {
    this.log.push({ time: this.time, id, action });
    if (this.log.length > 512) this.log.shift();
  }
  clear(): void {
    this.elite.clear();
    this.relief.clear();
    this.support.clear();
    this.security.clear();
    this.maritime.clear();
    this.air.clear();
    for (const u of this.friendly) this.population.civil?.cancelTask(u.person, 'Military lifecycle cleared');
    this.friendly = [];
    this.byId.clear();
    this.patrols = [];
    this.places.clear();
    this.log = [];
    this.time = this.sense = 0;
    this.mobilised = false;
    this.patrolStops = [];
    this.roadRecovery.clear();
    this.clearanceRequests = 0;
  }
  *resetSteps(): Generator<void> {
    this.clear();
    this.epoch = this.physics.life.epoch;
    const layout = this.city.strategic,
      civil = this.population.civil;
    if (!layout || !civil) return;
    this.air.prepare();
    yield;
    const parcel = layout.parcels.find((p) => p.id === 'military')!;
    const privateBuildings = new Set(parcel.buildings);
    const duties = (this.city.places ?? []).filter(
      (p) =>
        p.access === 'military' &&
        civil.network.available(p) &&
        ['home', 'desk', 'workbench', 'maintenance', 'seat', 'staff'].includes(p.kind),
    );
    for (const p of duties) {
      const list = this.places.get(p.buildingId!) ?? [];
      list.push(p);
      this.places.set(p.buildingId!, list);
    }
    const seen = new Set<string>(),
      spawns: WalkNode[] = [];
    for (const n of civil.network.nodes) {
      if (
        n.buildingId === undefined ||
        !privateBuildings.has(n.buildingId) ||
        !n.layer.startsWith('interior:') ||
        !this.places.has(n.buildingId)
      )
        continue;
      const key = `${n.x.toFixed(1)}:${n.y.toFixed(1)}:${n.z.toFixed(1)}`;
      if (seen.has(key)) continue;
      seen.add(key);
      spawns.push(n);
    }
    if (spawns.length < 400)
      throw new Error(`Military roster needs 400 supported base positions; found ${spawns.length}`);
    const network = this.population.network;
    for (const district of this.city.districts ?? []) {
      if (district.id === 'military') continue;
      const approach = network.approach(district, false, 4.8);
      if (approach) this.patrolStops.push({ node: approach.node, name: district.name });
      yield;
    }
    const edges = parcel.roads
      .map((id) => network.edges.get(id)!)
      .filter(
        (e) =>
          militaryZone(this.city, network.nodes.get(e.from)!) &&
          militaryZone(this.city, network.nodes.get(e.to)!),
      );
    for (let i = 0; i < 40; i++) {
      // Public patrols begin already on duty across the public district network.
      // Do not funnel all forty occupied vehicles through the same base gate.
      const publicEdges =
        this.city.streetLife && i % 5 !== 0
          ? [...network.edges.values()].filter((e) => !e.restricted && network.open(e, true, 5.2))
          : edges;
      let edge = edges[i % edges.length],
        from = edge.from,
        point = network.waypoint(edge, from, 0.35);
      for (let attempt = 0; attempt < publicEdges.length * 4; attempt++) {
        const candidate = publicEdges[(i * 17 + attempt) % publicEdges.length];
        const entry = attempt % 2 ? candidate.to : candidate.from;
        const at = network.waypoint(
          candidate,
          entry,
          0.16 + (Math.floor(attempt / publicEdges.length) % 4) * 0.2,
        );
        const end = network.waypoint(candidate, entry),
          yaw = Math.atan2(at.x - end.x, at.z - end.z);
        if (
          this.population.cars.some((car) =>
            overlapsRoadBody(
              { ...at, yaw, width: 2.9, length: 6.8 },
              { x: car.p.x, z: car.p.z, yaw: car.yaw, width: car.width, length: car.length },
              0.35,
            ),
          )
        )
          continue;
        edge = candidate;
        from = entry;
        point = at;
        break;
      }
      const end = network.waypoint(edge, from);
      const car = this.population.createCar(`military:patrol:${i}`, 'humvee', point, from);
      car.controlled = true;
      car.color = 0x778b5e;
      car.name = `Alder patrol ${i + 1}`;
      car.state = 'enroute';
      car.speed = 8;
      car.yaw = Math.atan2(point.x - end.x, point.z - end.z);
      car.q.setFromAxisAngle(new THREE.Vector3(0, 1, 0), car.yaw);
      car.previousQ.copy(car.q);
      car.body.setRotation(car.q, false);
      car.plan = [end];
      const patrol: Patrol = { car, crew: [], index: i, turns: 0, phase: 'patrol', timer: 0, checks: 0 };
      for (let seat = 0; seat < 5; seat++) {
        const u = this.createFriendly(
          i * 5 + seat,
          point,
          seat === 0 ? 'driver' : seat === 1 ? 'gunner' : 'patrol',
        );
        u.state = 'aboard';
        u.vehicle = car;
        u.seat = seat;
        u.person.carrier = car.id;
        u.person.exposedSeat = seat === 1;
        patrol.crew.push(u);
      }
      this.patrols.push(patrol);
      this.extendPatrol(patrol);
      yield;
    }
    for (let i = 0; i < 400; i++) {
      const node = spawns[Math.floor((i * spawns.length) / 400)];
      const options = this.places
        .get(node.buildingId!)!
        .filter((p) => civil.network.nodes[civil.network.places.get(p.id)!].layer === node.layer);
      const nearest = (options.length ? options : this.places.get(node.buildingId!)!)
        .slice()
        .sort(
          (a, b) => (a.x - node.x) ** 2 + (a.z - node.z) ** 2 - ((b.x - node.x) ** 2 + (b.z - node.z) ** 2),
        );
      const u = this.createFriendly(
        200 + i,
        this.air.initialPoint(200 + i) ?? node,
        ['reserve', 'maintenance', 'medical relief', 'equipment'][i % 4],
      );
      u.home = nearest[0];
      u.duty = nearest[Math.min(1, nearest.length - 1)];
      if (this.air.assign(u)) {
        if (i % 12 === 11) yield;
        continue;
      }
      u.person.layer = node.layer;
      u.retry = (i % 24) * 0.3;
      civil.resume(u.person, node.layer);
      u.person.controlled = true;
      u.person.state = 'wait';
      if (i % 12 === 11) yield;
    }
    yield* this.support.resetSteps();
    yield* this.security.resetSteps();
    yield* this.maritime.resetSteps();
    yield* this.elite.resetSteps();
    this.syncSeats();
    this.physics.world.propagateModifiedBodyPositionsToColliders();
    this.record(
      'friendly',
      '600 original soldiers: 200 in forty five-person patrols; the remaining 400 include 74 support transport crew',
    );
  }
  createFriendly(index: number, point: Vec, role: string, elite = false): Soldier {
    const id = elite ? `crew:military:elite:${index - 600}` : `crew:military:friendly:${index}`,
      person = this.population.createCrew(id, point);
    person.faction = 'friendly';
    person.name = `Garrison ${role} ${index + 1}`;
    person.color = elite ? 0x262a2e : 0x77875e;
    person.speed = elite ? BALANCE.eliteSpeed : 2.3;
    person.elite = elite;
    person.combatHealth = elite ? BALANCE.eliteHealth : 1;
    person.scale = 1;
    const u: Soldier = {
      id,
      person,
      index,
      faction: 'friendly',
      state: 'base',
      role,
      task: '',
      sequence: 0,
      retry: 0,
      checks: 0,
      route: [],
      cursor: 0,
      cooldown: 0,
      think: (index % 30) / 30,
    };
    this.friendly.push(u);
    this.byId.set(u.id, u);
    return u;
  }
  private extendPatrol(p: Patrol): void {
    if (p.car.plan.length > 5) return;
    const network = this.population.network,
      points = p.car.plan.slice();
    if (!points.length && p.car.state === 'parked') {
      const stop =
        !p.baseOnly && p.index % 5 !== 0
          ? this.patrolStops[(p.index + p.turns) % this.patrolStops.length]
          : undefined;
      const destination = stop?.node ?? network.nearest(p.car.home);
      const point = network.nodes.get(destination);
      if (point) {
        const route = this.population.plannedRoute(
          p.car,
          { ...point, y: point.y ?? 0.36 },
          destination,
          true,
        );
        if (route.length) {
          p.car.plan = route;
          p.destination = stop?.name ?? 'Garrison perimeter';
          p.turns++;
          p.car.state = 'enroute';
        }
      }
      return;
    }
    let node = points.at(-1)?.node ?? p.car.node,
      prior = points.at(-1)?.edge;
    // Four out of five patrols have real public district destinations; the
    // remaining eight retain base coverage. All forty keep their original crew.
    if (!p.baseOnly && p.index % 5 !== 0 && this.patrolStops.length) {
      const previous =
        prior === undefined ? p.car.previousNode : network.other(network.edges.get(prior)!, node);
      for (let attempt = 0; attempt < this.patrolStops.length; attempt++) {
        const stop = this.patrolStops[(p.index + p.turns + attempt) % this.patrolStops.length];
        if (stop.node === node) continue;
        const route = network.directionalRoute(node, stop.node, previous, undefined, true, p.car.length);
        if (route.length < 2) continue;
        for (let i = 1; i < route.length; i++)
          points.push(...network.points(network.edgeBetween(route[i - 1], route[i])!, route[i - 1]));
        p.turns += attempt + 1;
        p.destination = stop.name;
        p.car.plan = network.roundTurns(points, p.car.length, p.car.p);
        p.car.state = 'enroute';
        return;
      }
    }
    for (let step = 0; step < 3; step++) {
      const options = (network.adjacent.get(node) ?? []).filter(
        (e) => e.id !== prior && e.restricted && network.open(e, true, p.car.length),
      );
      if (!options.length) break;
      const edge = options[(p.index + p.turns++) % options.length];
      points.push(...network.points(edge, node));
      prior = edge.id;
      node = network.other(edge, node);
    }
    p.car.plan = network.roundTurns(points, p.car.length, p.car.p);
    p.destination = 'Garrison perimeter';
    if (p.car.plan.length) p.car.state = 'enroute';
  }
  blocked(car: TrafficVehicle): void {
    if (
      (!car.id.startsWith('military:') && !car.blockedBy.startsWith('military:')) ||
      (this.roadRecovery.get(car.id) ?? 0) > this.time
    )
      return;
    this.roadRecovery.set(car.id, this.time + 3);
    if (this.population.requestPedestrianClearance(car, true)) {
      const soldier = this.byId.get(car.blockedBy);
      if (soldier && !soldier.person.carrier) {
        this.population.civil?.cancelTask(soldier.person, 'Stepping clear of original military transport');
        soldier.task = '';
      }
      this.clearanceRequests++;
      return;
    }
    const obstruction = this.population.cars.find((other) => other.id === car.blockedBy);
    const parked =
      obstruction &&
      this.patrols.find(
        (p) =>
          p.car === obstruction &&
          ['inspection', 'boarding'].includes(p.phase) &&
          this.healthy(p.crew[0]) &&
          p.crew[0].person.carrier,
      );
    if (parked) {
      const body = parked.car;
      for (const sign of [-1, 1]) {
        const shift = new THREE.Vector3(sign * (body.width + 1.1), 0, -1.5).applyQuaternion(body.q);
        const end = body.p.clone().add(shift),
          support = this.physics.ground(end, 0.35, 0.7);
        if (!support) continue;
        end.y = support.y;
        const half = body.collider.halfExtents()!,
          center = body.collider.translation();
        if (
          !this.physics.occupiedBoxClear(
            center,
            { x: body.width / 2 + 0.12, y: half.y, z: body.length / 2 + 0.15 },
            body.q,
            shift,
            body.body,
          )
        )
          continue;
        body.plan = [{ ...end, maxSpeed: 2.2 }];
        body.state = 'enroute';
        body.speed = 2.2;
        parked.phase = 'clearing';
        this.clearanceRequests++;
        this.record(body.id, 'Original driver pulling the inspection vehicle clear of the access lane');
        return;
      }
    }
    if (
      obstruction &&
      (obstruction.blockedBy === car.id || ['wreck', 'parked', 'deployed'].includes(obstruction.state))
    ) {
      if ([4.5, 2.2].some((distance) => this.population.reverseForClearance(car, distance))) {
        this.clearanceRequests++;
        this.record(car.id, 'Reversing through real clearance to release a blocked military route');
        return;
      }
    }
    const patrol = this.patrols.find((p) => p.car === car);
    if (
      patrol &&
      patrol.phase === 'patrol' &&
      (car.blockedBy === 'structure' || car.blockedBy.startsWith('road:'))
    ) {
      const end = car.plan.at(-1);
      if (end) {
        const route = this.population.plannedRoute(
          car,
          end,
          end.node ?? this.population.network.nearest(end),
          true,
        );
        if (route.length) {
          car.plan = route;
          this.clearanceRequests++;
        }
      }
    }
  }
  private healthy(u: Soldier): boolean {
    return (
      !u.person.fatal &&
      !['incapacitated', 'falling', 'carried', 'recovering'].includes(u.person.state) &&
      !['lost', 'withdrawn'].includes(u.state)
    );
  }
  combatCapacity(): number {
    return this.friendly.filter((u) => this.healthy(u)).length;
  }
  isGunner(u: Soldier): boolean {
    return (
      u.seat === 1 &&
      !!u.vehicle &&
      isMilitaryVehicle(u.vehicle.kind) &&
      MILITARY_SPECS[u.vehicle.kind].gun > 0
    );
  }
  mobilise(): void {
    if (this.mobilised) return;
    this.relief.clear();
    this.mobilised = true;
    for (const u of this.friendly) {
      if (!this.healthy(u)) continue;
      if (u.assignment === 'naval') continue;
      this.population.civil?.cancelTask(u.person, 'Mobilising the original defender');
      u.task = '';
      u.person.controlled = true;
      if (u.state === 'base') u.state = 'deployed';
    }
    for (const p of this.patrols) {
      p.phase = 'braking';
      p.car.speed = 0;
    }
    this.support.mobilise();
    this.maritime.mobilise();
    this.air.mobilise();
    this.record('friendly', 'Original roster mobilising; no additional defenders spawned');
  }
  standDown(): void {
    this.mobilised = false;
    for (const u of this.friendly) {
      if (!this.healthy(u) || u.state === 'aboard') continue;
      this.population.civil?.cancelTask(u.person, 'Original defender standing down');
      u.target = undefined;
      u.goal = undefined;
      u.planning = undefined;
      u.route = [];
      u.person.controlled = true;
      u.state = u.vehicle ? 'returning' : 'base';
      u.task = '';
      u.retry = 0;
    }
    for (const p of this.patrols) {
      p.phase = p.crew.every((u) => u.state === 'aboard' || !this.healthy(u)) ? 'patrol' : 'boarding';
      p.timer = 0;
      p.car.speed = 8;
    }
    this.support.standDown();
    this.maritime.standDown();
    this.air.standDown();
    this.elite.standDown();
    this.record('friendly', 'Survivors returning to their original assignments');
  }
  returnToVehicle(u: Soldier, dt: number): boolean {
    if (u.state === 'aboard') return true;
    const car = u.vehicle,
      civil = this.population.civil;
    if (!car || !civil || !this.healthy(u) || u.transition || !civil.prepareForTask(u.person)) return false;
    const doors = [-1, 1]
      .map((side) =>
        car.p
          .clone()
          .add(
            new THREE.Vector3(
              side * (car.width / 2 + 0.7),
              0,
              Math.min(1.35, ((u.seat ?? 0) - 2) * 0.72),
            ).applyQuaternion(car.q),
          ),
      )
      .map((point) => {
        const ground = this.physics.ground(point, 0.4, 1);
        if (ground) point.y = ground.y;
        return point;
      })
      .sort((a, b) => a.distanceToSquared(u.person.p) - b.distanceToSquared(u.person.p));
    const direct = doors.find(
      (point) =>
        point.distanceTo(u.person.p) < 3 &&
        this.physics.sweepFoot(u.person.p, point.clone().sub(u.person.p)) >= 0.999,
    );
    if (direct) {
      u.person.controlled = true;
      if (this.population.footClear(u.person, direct)) this.population.walkTo(u.person, direct, dt, 2.3);
      if (u.person.p.distanceTo(direct) < 0.25) {
        civil.cancelTask(u.person, 'Original support worker reached the vehicle door');
        u.task = '';
        u.state = 'exiting';
        u.transition = { from: u.person.p.clone(), to: this.seatPosition(u), time: 0, boarding: true };
      }
      return false;
    }
    const task = civil.task(u.person);
    if (task?.token === u.task && task.state === 'active') return false;
    if (this.time < u.retry) return false;
    u.retry = this.time + 3;
    for (const door of doors) {
      const node = civil.network
        .near(door, 7, 'street')
        .filter(
          (n) =>
            civil.network.lineOpen(n, door) &&
            this.physics.sweepFoot(n, { x: door.x - n.x, y: door.y - n.y, z: door.z - n.z }) >= 0.999,
        )
        .sort((a, b) => Math.hypot(a.x - door.x, a.z - door.z) - Math.hypot(b.x - door.x, b.z - door.z))[0];
      if (!node) continue;
      const token = `military-return:${u.id}:${++u.sequence}`;
      if (civil.assignTask(u.person, civil.nodePlace(node), token, 'returning to transport', 0.1)) {
        u.task = token;
        break;
      }
    }
    return false;
  }
  beginExit(u: Soldier): boolean {
    const car = u.vehicle;
    if (!car || u.state !== 'aboard' || car.actualSpeed > 0.1) return false;
    const seat = u.seat ?? 0,
      sign = seat % 2 ? -1 : 1;
    for (const [side, along] of [sign, -sign].flatMap((side) =>
      [Math.min(1.35, (seat - 2) * 0.72), -car.length * 0.3, car.length * 0.3].map((along) => [side, along]),
    )) {
      const point = car.p
        .clone()
        .add(new THREE.Vector3(side * (car.width / 2 + 0.7), 0, along).applyQuaternion(car.q));
      const ground = this.physics.ground(point, 0.4, 1);
      if (
        !ground ||
        Math.abs(ground.y - car.p.y) > 0.55 ||
        this.physics.blocked(point.x, point.z, 0.25, ground.y + 0.85, car.body)
      )
        continue;
      point.y = ground.y;
      if (!this.population.footClear(u.person, point)) continue;
      if (
        !this.physics.occupiedBoxClear(
          { x: u.person.p.x, y: u.person.p.y + 0.8, z: u.person.p.z },
          { x: 0.23, y: 0.75, z: 0.23 },
          UPRIGHT,
          point.clone().sub(u.person.p),
          car.body,
        )
      )
        continue;
      u.state = 'exiting';
      u.person.controlled = true;
      u.person.hidden = false;
      u.transition = { from: u.person.p.clone(), to: point, time: 0, boarding: false };
      this.record(u.id, 'Original soldier leaving assigned vehicle seat');
      return true;
    }
    return false;
  }
  private transition(u: Soldier, dt: number): void {
    const t = u.transition,
      car = u.vehicle;
    if (!t || !car) return;
    const age = Math.min(0.65, t.time + dt),
      point = t.from.clone().lerp(t.to, age / 0.65),
      delta = point.clone().sub(u.person.p);
    if (
      !this.physics.occupiedBoxClear(
        { x: u.person.p.x, y: u.person.p.y + 0.8, z: u.person.p.z },
        { x: 0.23, y: 0.75, z: 0.23 },
        UPRIGHT,
        delta,
        car.body,
      )
    )
      return;
    u.person.previous.copy(u.person.p);
    u.person.p.copy(point);
    u.person.state = 'walk';
    t.time = age;
    if (age < 0.65) return;
    u.transition = undefined;
    u.person.velocity.set(0, 0, 0);
    if (t.boarding) {
      u.state = 'aboard';
      u.person.carrier = car.id;
      u.person.exposedSeat = this.isGunner(u);
    } else {
      u.state = this.mobilised ? 'deployed' : 'inspection';
      u.person.carrier = undefined;
      u.person.exposedSeat = false;
      u.person.layer = 'street';
      u.person.state = 'wait';
    }
  }
  private baseDuty(u: Soldier, dt: number): void {
    const civil = this.population.civil;
    if (!civil || !u.home) return;
    u.retry -= dt;
    if (u.retry > 0) return;
    const task = civil.task(u.person);
    if (u.task && task?.token === u.task && task.state === 'active') {
      u.retry = 0.4;
      return;
    }
    if (u.task && task?.token === u.task && task.state === 'completed') {
      u.checks++;
      u.task = '';
      u.retry = 0.4;
      return;
    }
    if (u.task) {
      u.task = '';
      u.retry = 3;
      return;
    }
    const place = u.checks % 2 ? (u.duty ?? u.home) : u.home;
    if (!civil.network.available(place)) {
      u.person.controlled = false;
      this.population.evacuate(u.person, place);
      u.retry = 5;
      return;
    }
    const token = `military-duty:${u.id}:${++u.sequence}`;
    if (
      civil.assignTask(
        u.person,
        place,
        token,
        ['seat', 'home'].includes(place.kind) ? 'resting' : 'inspecting',
        12 + (u.index % 11),
      )
    ) {
      u.task = token;
      u.retry = 0.4;
    } else {
      u.person.controlled = true;
      u.person.state = 'wait';
      u.retry = 2;
    }
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch) return;
    this.time += dt;
    this.sense -= dt;
    for (const u of this.friendly) {
      u.cooldown = Math.max(0, u.cooldown - dt);
      if (!u.person.carrier && !u.transition && this.population.stepYield(u.person, dt)) continue;
      if (u.vehicle?.state === 'wreck' && u.person.carrier) {
        u.person.carrier = undefined;
        u.person.exposedSeat = false;
        u.transition = undefined;
        u.person.hidden = false;
        this.population.incapacitate(u.person, u.vehicle.p, 1.8);
        u.person.velocity.add(u.vehicle.body.linvel());
        u.state = 'incapacitated';
        this.record(u.id, 'Original occupied-vehicle casualty released once');
      }
      if (!this.healthy(u)) {
        if (u.person.fatal || u.person.state === 'incapacitated') u.state = 'incapacitated';
        continue;
      }
      if (u.transition) {
        this.transition(u, dt);
        continue;
      }
      if (u.state === 'incapacitated') {
        u.state = u.vehicle ? 'returning' : 'base';
        u.person.controlled = true;
      }
      if (!this.mobilised && u.state === 'base' && !u.assignment) this.baseDuty(u, dt);
    }
    for (const patrol of this.patrols) {
      const car = patrol.car;
      if (car.state === 'wreck') continue;
      const driver = patrol.crew[0];
      if (!this.healthy(driver)) {
        car.speed = 0;
        continue;
      }
      if (patrol.phase === 'clearing') {
        car.speed = 2.2;
        if (!car.plan.length) {
          patrol.phase = 'inspection';
          patrol.timer = 0;
        }
      } else if (patrol.phase === 'patrol' && !this.mobilised) {
        car.speed = (patrol.baseOnly ? 12 : 8) * (0.45 + 0.55 * car.health);
        this.extendPatrol(patrol);
        if (this.sense <= 0 && this.population.hazardAt(car.p)) {
          patrol.phase = 'braking';
          car.speed = 0;
        }
      } else if (patrol.phase === 'braking') {
        car.speed = 0;
        if (car.actualSpeed > 0.1) continue;
        car.plan = [];
        car.state = 'parked';
        patrol.timer += dt;
        for (const u of patrol.crew.slice(1))
          if ((!this.mobilised || !this.isGunner(u)) && patrol.timer > (u.seat ?? 0) * 0.7) this.beginExit(u);
        if (
          patrol.crew
            .slice(1)
            .every(
              (u) =>
                (this.mobilised && this.isGunner(u)) ||
                (u.state !== 'aboard' && u.state !== 'exiting') ||
                !this.healthy(u),
            )
        ) {
          patrol.phase = 'inspection';
          patrol.timer = 0;
        }
      } else if (patrol.phase === 'inspection' && !this.mobilised) {
        patrol.timer += dt;
        for (const u of patrol.crew.slice(1))
          if (this.healthy(u) && u.state === 'inspection') {
            const point = car.p
              .clone()
              .add(
                new THREE.Vector3(
                  (u.seat! % 2 ? -1 : 1) * (car.width / 2 + 1.6),
                  0,
                  (u.seat! - 2) * 1.7,
                ).applyQuaternion(car.q),
              );
            this.population.walkTo(u.person, point, dt, 2.3);
            if (u.person.p.distanceTo(point) < 0.4) u.person.state = 'inspecting';
          }
        if (patrol.timer > 15 && !this.population.hazardAt(car.p)) {
          patrol.checks++;
          patrol.phase = 'boarding';
          for (const u of patrol.crew.slice(1)) if (this.healthy(u)) u.state = 'returning';
        }
      } else if (patrol.phase === 'boarding' && !this.mobilised) {
        for (const u of patrol.crew)
          if (this.healthy(u) && u.state === 'returning') {
            const side = u.seat! % 2 ? -1 : 1,
              door = car.p
                .clone()
                .add(
                  new THREE.Vector3(
                    side * (car.width / 2 + 0.7),
                    0,
                    Math.min(1.35, (u.seat! - 2) * 0.72),
                  ).applyQuaternion(car.q),
                );
            this.population.walkTo(u.person, door, dt, 2.3);
            if (u.person.p.distanceTo(door) < 0.25) {
              u.state = 'exiting';
              u.transition = { from: u.person.p.clone(), to: this.seatPosition(u), time: 0, boarding: true };
            }
          }
        if (patrol.crew.every((u) => u.state === 'aboard' || !this.healthy(u))) {
          patrol.phase = 'patrol';
          car.state = 'enroute';
          this.extendPatrol(patrol);
        }
      }
    }
    this.support.update(dt);
    this.security.update(dt);
    this.maritime.update(dt);
    this.air.update(dt);
    this.relief.update(dt);
    this.elite.update(dt);
    if (this.sense <= 0) this.sense = 0.5;
  }
  seatPosition(u: Soldier): THREE.Vector3 {
    const car = u.vehicle!;
    if (car.kind !== 'humvee') {
      const seat = u.seat ?? 0;
      let p: THREE.Vector3;
      if (this.isGunner(u)) p = new THREE.Vector3(0, car.kind === 'apc' ? 1.45 : 1.12, 0.35);
      else if (car.kind === 'military-sedan')
        p = new THREE.Vector3(seat % 2 ? 0.45 : -0.45, 0.4, seat < 2 ? -0.6 : 0.7);
      else if (car.kind === 'troop-truck')
        p =
          seat < 2
            ? new THREE.Vector3(seat ? 0.48 : -0.48, 0.85, -2.65)
            : new THREE.Vector3(seat % 2 ? 0.7 : -0.7, 0.88, -1.45 + Math.floor((seat - 2) / 2) * 0.54);
      else if (car.kind === 'apc')
        p =
          seat === 0
            ? new THREE.Vector3(-0.6, 0.85, -2)
            : new THREE.Vector3(seat % 2 ? 0.65 : -0.65, 0.88, -1.45 + Math.floor((seat - 2) / 2) * 1.05);
      else p = new THREE.Vector3(seat === 0 ? -0.5 : 0.5, 0.65, seat === 0 ? -0.95 : 0.7);
      return p.applyQuaternion(car.q).add(car.p);
    }
    const seats = [
      [-0.56, 0.69, -0.63],
      [0, 1.18, 0.39],
      [0.56, 0.69, -0.63],
      [-0.56, 0.69, 0.78],
      [0.56, 0.69, 0.78],
    ];
    return new THREE.Vector3(...(seats[u.seat ?? 0] as [number, number, number]))
      .applyQuaternion(car.q)
      .add(car.p);
  }
  syncSeats(): void {
    for (const u of this.friendly)
      if (u.state === 'aboard' && u.vehicle && this.healthy(u)) {
        const p = u.person;
        p.previous.copy(p.p);
        p.p.copy(this.seatPosition(u));
        const gunner = this.isGunner(u);
        p.yaw = u.vehicle.yaw + (gunner ? (u.vehicle.turretYaw ?? 0) : 0);
        p.state = gunner ? 'turret' : 'sitting';
        p.seatHeight = gunner ? undefined : 0.5;
        p.exposedSeat = gunner;
        p.hidden = false;
        p.controlled = true;
      }
  }
  snapshot(): Record<string, number> {
    return {
      friendlyRoster: this.friendly.length,
      friendlyCombatCapable: this.combatCapacity(),
      militaryLaneClearances: this.clearanceRequests,
      friendlyAboard: this.friendly.filter((u) => u.state === 'aboard').length,
      friendlyBase: this.friendly.filter((u) => u.state === 'base').length,
      friendlyIncapacitated: this.friendly.filter((u) => u.state === 'incapacitated').length,
      friendlyPatrolVehicles: this.patrols.length,
      friendlyPatrolPersonnel: this.patrols.reduce((n, p) => n + p.crew.length, 0),
      militaryWorkSessions: this.friendly.reduce((n, u) => n + u.checks, 0),
      militaryInspections: this.patrols.reduce((n, p) => n + p.checks, 0),
      ...this.support.snapshot(),
      ...this.security.snapshot(),
      ...this.relief.snapshot(),
      ...this.elite.snapshot(),
      militaryNavalCrew: this.maritime.crew.length,
      militaryNavalVisits: this.maritime.visits,
      militaryNavalChecks: this.maritime.checks,
      militaryAirCrew: this.air.crew.length,
      militaryAirSorties: this.air.sorties,
    };
  }
  inspect(): unknown {
    return {
      ...this.snapshot(),
      support: this.support.inspect(),
      security: this.security.inspect(),
      maritime: this.maritime.inspect(),
      air: this.air.inspect(),
      relief: this.relief.inspect(),
      patrols: this.patrols.map((p) => ({
        id: p.car.id,
        p: p.car.p.toArray(),
        phase: p.phase,
        destination: p.destination,
        speed: p.car.actualSpeed,
        blocked: p.car.blockedBy,
        seats: p.crew.map((u) => ({ id: u.id, role: u.role, state: u.state })),
      })),
      people: this.friendly.map((u) => ({
        id: u.id,
        state: u.state,
        role: u.role,
        p: u.person.p.toArray(),
        home: u.home?.id,
        checks: u.checks,
      })),
      log: this.log,
    };
  }
  dispose(): void {
    this.clear();
    this.security.dispose();
    this.air.dispose();
  }
}
